'use client';

import { useState, useMemo, useEffect } from 'react';
import { Tool, getToolUrlPath } from '@/lib/data';
import Link from 'next/link';
import { Search, X, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { getLocaleFromPathname, toFrenchProfessionSlug, translations, type Dictionary } from '@/lib/i18n';
import SmartLogo from '@/components/tool/SmartLogo';


interface ProfessionToolsListProps {
  initialTools: Tool[];
  professionSlug: string;
}

export default function ProfessionToolsList({ initialTools, professionSlug }: ProfessionToolsListProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname || '');
  const t: Dictionary = translations[locale] || translations['en'];
  const localizedSlug = locale === 'fr' ? toFrenchProfessionSlug(professionSlug) : professionSlug;
  const localePrefix = locale === 'fr' ? '/fr' : '';
  const router = useRouter();
  const searchParams = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [pricingFilter, setPricingFilter] = useState<string>('all');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showAllTags, setShowAllTags] = useState(false);
  const [renderedAt] = useState(() => Date.now());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(8);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(8);
  }, [debouncedSearch, selectedCategory, pricingFilter, selectedTag]);

  const showMore = () => {
    setVisibleCount(prev => prev + 12);
  };
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedCategory !== 'all') count++;
    if (pricingFilter !== 'all') count++;
    if (selectedTag) count++;
    return count;
  }, [selectedCategory, pricingFilter, selectedTag]);

  const industries = [
    { name: t.professions['legal'], slug: 'legal' },
    { name: t.professions['finance'], slug: 'finance' },
    { name: t.professions['education'], slug: 'education' },
    { name: t.professions['real-estate'], slug: 'real-estate' },
  ];

  const categories = useMemo(() => {
    const cats = new Set<string>();
    initialTools.forEach(t => {
      if (Array.isArray(t.categories)) {
        t.categories.forEach(c => cats.add(c));
      }
    });
    return Array.from(cats).sort();
  }, [initialTools]);

  // Derive all unique tags from tools dynamically
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    initialTools.forEach(t => {
      if (Array.isArray((t as any).tags)) {
        (t as any).tags.forEach((tag: string) => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [initialTools]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const filteredTools = useMemo(() => {
    return initialTools.filter(tool => {
      const toolTags: string[] = Array.isArray((tool as any).tags) ? (tool as any).tags : [];
      const matchesSearch = 
        tool.name.toLowerCase().includes(debouncedSearch.toLowerCase()) || 
        tool.description_short.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        toolTags.some(tag => typeof tag === 'string' && tag.toLowerCase().includes(debouncedSearch.toLowerCase()));
      
      const toolCategories = Array.isArray(tool.categories) ? tool.categories : [];
      const matchesCategory = selectedCategory === 'all' || toolCategories.includes(selectedCategory);
      
      const toolPricing = tool.pricing?.[0]?.plan?.toLowerCase() || '';
      const matchesPricing = pricingFilter === 'all' || (
        pricingFilter === 'Free' ? toolPricing.includes('free') :
        pricingFilter === 'Paid' ? (toolPricing.includes('paid') || toolPricing.includes('subscription') || toolPricing.includes('pro')) :
        pricingFilter === 'Enterprise' ? toolPricing.includes('enterprise') :
        toolPricing.includes(pricingFilter.toLowerCase())
      );

      const matchesTag = !selectedTag || toolTags.includes(selectedTag);
      
      return matchesSearch && matchesCategory && matchesPricing && matchesTag;
    });
  }, [initialTools, debouncedSearch, selectedCategory, pricingFilter, selectedTag]);

  const resetFilters = () => {
    setSearch('');
    setSelectedCategory('all');
    setPricingFilter('all');
    setSelectedTag(null);
  };

  const hasActiveFilters = !!(search || selectedCategory !== 'all' || pricingFilter !== 'all' || selectedTag);

  const visibleTags = useMemo(() => {
    if (showAllTags) return availableTags;
    // Show 6 tags by default
    let tags = availableTags.slice(0, 6);
    // Ensure selected tag is always visible
    if (selectedTag && !tags.includes(selectedTag)) {
      tags = [...availableTags.slice(0, 5), selectedTag];
    }
    return tags;
  }, [availableTags, showAllTags, selectedTag]);

  const hiddenTagsCount = availableTags.length - visibleTags.length;

  return (
    <div className="space-y-8">
      {/* Filters Bar */}
      <div className="bg-background/95 supports-[backdrop-filter]:bg-background/80 backdrop-blur-md border border-border rounded-2xl p-4 sm:p-6 shadow-md sticky top-20 z-40 transition-all duration-300">
        
        {/* Desktop Layout: visible on xl screen and above */}
        <div className="hidden xl:flex flex-row gap-6 items-center">
          {/* Search */}
          <div className="relative w-full lg:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder={t.professionPage.searchPlaceholder} 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 bg-muted/50 border-border focus:ring-primary rounded-xl"
              aria-label="Search AI tools"
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Category Select */}
          <div className="w-full lg:max-w-[11.25rem]">
            <Select value={selectedCategory} onValueChange={(value: string | null) => setSelectedCategory(value || 'all')}>
              <SelectTrigger className="h-11 bg-muted/50 rounded-xl" aria-label="Filter by category">
                <SelectValue>{t.professionPage.categoryFilter}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.professionPage.categoryFilter}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pricing Select */}
          <div className="w-full lg:max-w-[11.25rem]">
            <Select value={pricingFilter} onValueChange={(value: string | null) => setPricingFilter(value || 'all')}>
              <SelectTrigger className="h-11 bg-muted/50 rounded-xl" aria-label="Filter by pricing">
                <SelectValue>{t.professionPage.pricingFilter}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.professionPage.pricingFilter}</SelectItem>
                <SelectItem value="Free">Free</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="h-6 w-px bg-border" />

          {/* Industry Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 xl:pb-0 scrollbar-hide">
            {industries.map((ind) => (
              <Button
                key={ind.slug}
                variant={professionSlug === ind.slug ? "default" : "ghost"}
                size="sm"
                onClick={() => router.push(`${localePrefix}/${locale === 'fr' ? toFrenchProfessionSlug(ind.slug) : ind.slug}`)}
                className={`rounded-xl h-10 px-4 text-xs font-bold uppercase tracking-wider transition-all ${
                  professionSlug === ind.slug 
                    ? 'bg-primary text-white shadow-md' 
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {ind.name}
              </Button>
            ))}
          </div>

          <div className="h-6 w-px bg-border" />

          {/* Reset & Summary */}
          <div className="flex items-center justify-between w-full xl:w-auto ml-auto gap-4">
            <span className="text-[0.625rem] font-bold text-muted-foreground uppercase tracking-widest bg-muted rounded-md px-2 py-1">
              {filteredTools.length} results
            </span>
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetFilters}
                className="text-primary hover:text-primary hover:bg-primary/10 rounded-lg h-9 text-xs font-bold uppercase tracking-wider"
              >
                Reset
              </Button>
            )}
          </div>
        </div>

        {/* Mobile/Tablet Layout: visible below xl screen */}
        <div className="xl:hidden flex flex-col gap-4">
          <div className="flex items-center gap-3">
            {/* Mobile Search input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder={t.professionPage.searchPlaceholder} 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 bg-muted/50 border-border focus:ring-primary rounded-xl w-full"
                aria-label="Search AI tools"
              />
              {search && (
                <button 
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Mobile Filter Menu Toggle Button */}
            <Button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              variant={mobileMenuOpen || activeFiltersCount > 0 ? "default" : "outline"}
              className="rounded-xl h-11 px-4 gap-2 flex items-center justify-center font-bold text-xs uppercase tracking-wider shrink-0"
            >
              <Filter className="h-4 w-4" />
              <span>{t.professionPage.filters}</span>
              {activeFiltersCount > 0 && (
                <span className="h-5 w-5 rounded-full bg-primary-foreground text-primary flex items-center justify-center text-[10px] font-black">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          </div>

          {/* Collapsible Filters Menu Overlay */}
          {mobileMenuOpen && (
            <div className="pt-4 border-t border-border/60 flex flex-col gap-5 animate-in slide-in-from-top-4 duration-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                
                {/* Category Select */}
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t.professionPage.categoryFilter}</span>
                  <Select value={selectedCategory} onValueChange={(value: string | null) => setSelectedCategory(value || 'all')}>
                    <SelectTrigger className="h-11 bg-muted/50 rounded-xl" aria-label="Filter by category">
                      <SelectValue>{t.professionPage.categoryFilter}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t.professionPage.categoryFilter}</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Pricing Select */}
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t.professionPage.pricingFilter}</span>
                  <Select value={pricingFilter} onValueChange={(value: string | null) => setPricingFilter(value || 'all')}>
                    <SelectTrigger className="h-11 bg-muted/50 rounded-xl" aria-label="Filter by pricing">
                      <SelectValue>{t.professionPage.pricingFilter}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t.professionPage.pricingFilter}</SelectItem>
                      <SelectItem value="Free">Free</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                      <SelectItem value="Enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

              </div>

              {/* Mobile Tag Checkboxes List instead of pills cloud */}
              {availableTags.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Filter by Tags</span>
                  <div className="grid grid-cols-2 gap-3 max-h-40 overflow-y-auto pr-2 ai-scrollbar border border-border/40 bg-muted/20 p-3.5 rounded-xl">
                    {availableTags.map((tag) => (
                      <label key={tag} className="flex items-center gap-2.5 cursor-pointer text-xs font-medium text-foreground/80 hover:text-primary select-none py-1">
                        <Checkbox
                          checked={selectedTag === tag}
                          onCheckedChange={() => setSelectedTag(selectedTag === tag ? null : tag)}
                        />
                        <span className="truncate">#{tag}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Mobile Industry Select */}
              <div className="flex flex-col gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Sectors</span>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                  {industries.map((ind) => (
                    <Button
                      key={ind.slug}
                      variant={professionSlug === ind.slug ? "default" : "ghost"}
                      size="sm"
                      onClick={() => {
                        router.push(`${localePrefix}/${locale === 'fr' ? toFrenchProfessionSlug(ind.slug) : ind.slug}`);
                        setMobileMenuOpen(false);
                      }}
                      className="rounded-xl h-9 px-3 text-[10px] font-bold uppercase tracking-wider shrink-0"
                    >
                      {ind.name}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Action Buttons inside Collapsible Menu */}
              <div className="flex items-center justify-between pt-4 border-t border-border/40 gap-4 mt-2">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest bg-muted rounded px-2.5 py-1">
                  {filteredTools.length} results
                </span>
                
                <div className="flex items-center gap-2">
                  {hasActiveFilters && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={resetFilters}
                      className="text-primary hover:bg-primary/10 rounded-lg text-xs font-bold uppercase"
                    >
                      Reset All
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => setMobileMenuOpen(false)}
                    className="rounded-lg text-xs font-bold uppercase px-4 bg-primary text-white"
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Tag Filter Pills: Only render on desktop to keep mobile screen compact */}
        {availableTags.length > 0 && (
          <div className="hidden xl:block mt-5 pt-5 border-t border-border/60">
            <p className="text-[0.625rem] font-bold text-muted-foreground uppercase tracking-widest mb-3">Filter by Tag</p>
            <div className="flex flex-wrap gap-2">
              {visibleTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                  aria-pressed={selectedTag === tag}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-200 ${
                    selectedTag === tag
                      ? 'bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20'
                      : 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/15 hover:border-primary/40'
                  }`}
                >
                  #{tag}
                </button>
              ))}
              {hiddenTagsCount > 0 && !showAllTags && (
                <button
                  onClick={() => setShowAllTags(true)}
                  className="px-3 py-1 rounded-full text-xs font-semibold border border-dashed border-muted-foreground/40 text-muted-foreground hover:bg-muted/50 transition-all duration-200"
                >
                  +{hiddenTagsCount} more
                </button>
              )}
              {showAllTags && availableTags.length > 6 && (
                <button
                  onClick={() => setShowAllTags(false)}
                  className="px-3 py-1 rounded-full text-xs font-semibold border border-dashed border-muted-foreground/40 text-muted-foreground hover:bg-muted/50 transition-all duration-200"
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tools Grid */}
      {filteredTools.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTools.slice(0, visibleCount).map((tool) => {
              const toolCats = Array.isArray(tool.categories) ? tool.categories : [];
              return (
                <div
                  key={tool.id}
                  className="group relative rounded-3xl border border-border/60 bg-card/60 dark:bg-card/40 backdrop-blur-sm overflow-hidden shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:shadow-lg hover:shadow-primary/10 hover:border-primary/40 transition-all duration-500 flex flex-col h-full hover:-translate-y-1.5"
                >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                
                <Link
                  href={`${localePrefix}/ai-for-${locale === 'fr' ? toFrenchProfessionSlug(tool.profession_slug) : tool.profession_slug}/${getToolUrlPath(tool.profession_slug, toolCats, tool.slug)}` }
                  className="relative p-6 flex-1 flex flex-col z-10"
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="h-12 w-12 rounded-2xl bg-muted/50 overflow-hidden flex items-center justify-center border border-border/40 backdrop-blur-sm group-hover:scale-110 group-hover:rotate-6 group-hover:border-primary/30 transition-all duration-500 shadow-sm">
                      <SmartLogo
                        logoUrl={tool.logo_url ?? null}
                        websiteUrl={tool.website_url ?? null}
                        name={tool.name}
                        size={48}
                        className="h-full w-full object-contain p-2"
                      />
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      {tool.created_at && (renderedAt - new Date(tool.created_at).getTime()) < 5 * 24 * 60 * 60 * 1000 && (
                        <Badge className="text-[10px] bg-success/10 text-success border border-success/30 hover:bg-success/20">
                          {t.professionPage.newBadge}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-[10px] bg-info/10 text-info border border-info/30 font-bold uppercase tracking-wider">
                        {t.professionPage.views.replace('{count}', (tool.view_count || 0).toString())}
                      </Badge>
                    </div>
                  </div>
                  <h3 className="text-xl font-black text-foreground group-hover:text-primary transition-colors mb-2 line-clamp-1 tracking-tight">
                    {tool.name}
                  </h3>
                  <p className="text-muted-foreground text-[0.8rem] line-clamp-3 leading-relaxed flex-1">
                    {tool.description_short}
                  </p>
                </Link>

                {/* Card Actions Footer */}
                <div className="relative z-20 px-6 pb-6 mt-auto flex items-center justify-between gap-2 border-t border-border/40 pt-4 bg-muted/5">
                  <div className="flex flex-wrap gap-1">
                    {Array.isArray((tool as any).tags) && ((tool as any).tags as string[]).slice(0, 1).map((tag: string) => (
                      <span key={tag} className="px-2 py-0.5 rounded-lg text-[9px] font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <Link
                    href={`${localePrefix}/compare?tool=${tool.slug}`}
                    className="text-[10px] font-black text-primary hover:underline uppercase tracking-wider flex items-center gap-1 shrink-0"
                  >
                    {t.professionPage.compare} ⇄
                  </Link>
                </div>
              </div>
            );
          })}
          </div>

          {visibleCount < filteredTools.length && (
            <div className="mt-12 flex justify-center">
              <Button
                onClick={showMore}
                variant="outline"
                size="lg"
                className="rounded-full px-8 font-bold border-primary/20 hover:border-primary/50 hover:bg-primary/5"
              >
                {t.professionPage.showMoreTools.replace('{count}', (filteredTools.length - visibleCount).toString())}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="py-24 text-center bg-card/60 dark:bg-card/40 border border-border/60 backdrop-blur-sm rounded-3xl shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-muted/50 border border-border/40 mb-4">
            <Filter className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-2">{t.professionPage.noToolsFound}</h3>
          <p className="text-muted-foreground mb-8">{t.professionPage.tryAdjustingFilters}</p>
          <Button variant="outline" onClick={resetFilters} className="rounded-xl">
            {t.professionPage.clearAllFilters}
          </Button>
        </div>
      )}
      
    </div>
  );
}
